import React, { FC, useState } from 'react'
import {
  Typography,
  TableRow,
  TableCell,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import { usePharmacistsQuery } from '@/features/auth'
import {
  useCreatePharmacistMutation,
  useUpdatePharmacistMutation,
  useDeletePharmacistMutation,
} from '@/features/internal/pharmacist'
import type { Pharmacist } from '@/features/auth/types/pharmacy'
import {
  DataTable,
  DialogShell,
  NotionButton,
  NotionTextField,
  PageLoading,
  PageScaffold,
} from '@/apps/latest/components/portal/notion'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'

type DialogMode = 'create' | 'edit' | 'delete' | null

export const PharmacistManagementPage: FC = () => {
  const { pharmacists, isLoading } = usePharmacistsQuery()
  const createMutation = useCreatePharmacistMutation()
  const updateMutation = useUpdatePharmacistMutation()
  const deleteMutation = useDeletePharmacistMutation()

  const [dialogMode, setDialogMode] = useState<DialogMode>(null)
  const [selectedPharmacist, setSelectedPharmacist] =
    useState<Pharmacist | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleOpenCreateDialog = () => {
    setFormData({ name: '', display_name: '' })
    setFormErrors({})
    setDialogMode('create')
  }

  const handleOpenEditDialog = (pharmacist: Pharmacist) => {
    setSelectedPharmacist(pharmacist)
    setFormData({
      name: pharmacist.name,
      display_name: pharmacist.display_name,
    })
    setFormErrors({})
    setDialogMode('edit')
  }

  const handleOpenDeleteDialog = (pharmacist: Pharmacist) => {
    setSelectedPharmacist(pharmacist)
    setDialogMode('delete')
  }

  const handleCloseDialog = () => {
    setDialogMode(null)
    setSelectedPharmacist(null)
    setFormData({ name: '', display_name: '' })
    setFormErrors({})
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}
    if (!formData.name.trim()) {
      errors.name = '名前は必須です'
    }
    if (!formData.display_name.trim()) {
      errors.display_name = '表示名は必須です'
    }
    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    setIsSubmitting(true)
    try {
      if (dialogMode === 'create') {
        await createMutation.mutateAsync({
          name: formData.name.trim(),
          display_name: formData.display_name.trim(),
        })
      } else if (dialogMode === 'edit' && selectedPharmacist) {
        await updateMutation.mutateAsync({
          id: selectedPharmacist.id,
          data: {
            name: formData.name.trim(),
            display_name: formData.display_name.trim(),
          },
        })
      }
      handleCloseDialog()
    } catch {
      // mutation 内で処理
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedPharmacist) return

    setIsSubmitting(true)
    try {
      await deleteMutation.mutateAsync(selectedPharmacist.id)
      handleCloseDialog()
    } catch {
      // mutation 内で処理
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <PageScaffold title="薬剤師管理">
        <PageLoading />
      </PageScaffold>
    )
  }

  const list = pharmacists?.pharmacists ?? []

  return (
    <PageScaffold
      title="薬剤師管理"
      meta={list.length > 0 ? `全 ${list.length} 件` : undefined}
      actions={
        <NotionButton
          variant="primary"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
        >
          薬剤師を追加
        </NotionButton>
      }
    >
      <DataTable
        columns={[
          { key: 'name', label: '名前' },
          { key: 'display_name', label: '表示名' },
          { key: 'pharmacy_name', label: '所属薬局' },
          { key: 'actions', label: '操作', align: 'right' },
        ]}
      >
        {list.length > 0 ? (
          list.map((pharmacist) => (
            <TableRow key={pharmacist.id}>
              <TableCell>{pharmacist.name}</TableCell>
              <TableCell>{pharmacist.display_name}</TableCell>
              <TableCell>{pharmacist.pharmacy_name}</TableCell>
              <TableCell align="right">
                {pharmacist.is_own ? (
                  <Stack direction="row" gap={0.5} justifyContent="flex-end">
                    <Tooltip title="編集">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditDialog(pharmacist)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="削除">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(pharmacist)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ) : (
                  <Typography sx={PORTAL_TEXT.cellMeta}>
                    他ユーザー登録
                  </Typography>
                )}
              </TableCell>
            </TableRow>
          ))
        ) : (
          <TableRow>
            <TableCell colSpan={4} align="center">
              <Typography sx={{ ...PORTAL_TEXT.empty, py: 4 }}>
                薬剤師が登録されていません
              </Typography>
            </TableCell>
          </TableRow>
        )}
      </DataTable>

      <DialogShell
        open={dialogMode === 'create' || dialogMode === 'edit'}
        onClose={handleCloseDialog}
        title={dialogMode === 'create' ? '薬剤師を追加' : '薬剤師を編集'}
        maxWidth="sm"
        disabled={isSubmitting}
        actions={
          <>
            <NotionButton variant="secondary" onClick={handleCloseDialog}>
              キャンセル
            </NotionButton>
            <NotionButton
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {dialogMode === 'create' ? '追加' : '保存'}
            </NotionButton>
          </>
        }
      >
        <Stack gap={2} sx={{ pt: 1 }}>
          <NotionTextField
            label="名前"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={!!formErrors.name}
            helperText={formErrors.name}
            autoFocus
          />
          <NotionTextField
            label="表示名"
            value={formData.display_name}
            onChange={(e) =>
              setFormData({ ...formData, display_name: e.target.value })
            }
            error={!!formErrors.display_name}
            helperText={formErrors.display_name}
          />
        </Stack>
      </DialogShell>

      <DialogShell
        open={dialogMode === 'delete'}
        onClose={handleCloseDialog}
        title="薬剤師を削除"
        maxWidth="xs"
        disabled={isSubmitting}
        actions={
          <>
            <NotionButton variant="secondary" onClick={handleCloseDialog}>
              キャンセル
            </NotionButton>
            <NotionButton
              variant="destructive"
              onClick={handleDelete}
              disabled={isSubmitting}
            >
              削除
            </NotionButton>
          </>
        }
      >
        <Typography sx={PORTAL_TEXT.rowLabel}>
          「{selectedPharmacist?.display_name}」を削除しますか？
        </Typography>
      </DialogShell>
    </PageScaffold>
  )
}
